import { Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import type { FC } from "react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import type { AddFinalCorrectionInvoiceRequestBody } from "../../../api/api";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../components/cancelButton/CancelButton";
import FormCheckbox from "../../components/formComponents/FormCheckbox";
import FormTextField from "../../components/formComponents/FormTextField";
import type { Currency } from "../../data/BaseEntity";
import type { SellerSelection } from "./invoiceSellerUtils";
import { AUTOMATIC_SELLER_SELECTION, buildCorrectionScopeFromSeller } from "./invoiceSellerUtils";
import InvoiceSellerSelect from "./InvoiceSellerSelect";

interface Props {
  readonly open: boolean;
  readonly orderId: string;
  readonly currency: Currency;
  readonly onClose: () => void;
  readonly onSubmit: (body: AddFinalCorrectionInvoiceRequestBody) => Promise<void>;
}

interface FormState {
  readonly reason: string;
  readonly sendByEmail: boolean;
  readonly seller: SellerSelection;
}

const FinalCorrectionInvoiceDialog: FC<Props> = ({ open, orderId, currency, onClose, onSubmit }) => {
  const { control, handleSubmit, reset } = useForm<FormState>({
    defaultValues: { reason: "Zmiana ilości świadczeń", sendByEmail: true, seller: AUTOMATIC_SELLER_SELECTION },
    mode: "onBlur",
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (open) {
      reset({ reason: "Zmiana ilości świadczeń", sendByEmail: true, seller: AUTOMATIC_SELLER_SELECTION });
    }
  }, [open, reset]);

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Faktura końcowa korygująca</DialogTitle>
      <DialogContent>
        <Stack
          direction="column"
          sx={{
            marginTop: 2,
          }}
        >
          <FormTextField
            name="reason"
            control={control}
            label="Powód korekty"
            multiline
            minRows={3}
            rules={{
              required: { value: true, message: "Powód korekty nie może być pusty" },
            }}
          />
          <InvoiceSellerSelect
            orderId={orderId}
            currency={currency}
            invoiceKind="advance"
            control={control}
            name="seller"
          />
          <FormCheckbox name="sendByEmail" control={control} label="Wyślij mailem do klienta" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <CancelButton
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
        />
        <AsyncLoadingButton
          onClick={handleSubmit(async (data) => {
            await onSubmit({
              reason: data.reason.trim(),
              sendByEmail: data.sendByEmail,
              ...buildCorrectionScopeFromSeller(data.seller),
            });
          })}
        >
          Utwórz
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default FinalCorrectionInvoiceDialog;
